import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import useCookies from 'views/common/hooks/useCookies';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
// Material UI icons
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LaunchIcon from '@mui/icons-material/Launch';

const PREFIX = 'Revzilla';

const classes = {
  toggle: `${PREFIX}-toggle`,
  logo: `${PREFIX}-logo`,
  root: `${PREFIX}-root`,
  close: `${PREFIX}-close`,
  logos: `${PREFIX}-logos`,
  info: `${PREFIX}-info`,
  description: `${PREFIX}-description`,
  links: `${PREFIX}-links`,
  back: `${PREFIX}-back`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.toggle}`]: {
    position: 'fixed',
    width: theme.typography.pxToRem(265),
    height: theme.typography.pxToRem(54),
    right: '1.5rem',
    bottom: '1.5rem',
    backgroundColor: 'rgba(17,17,17,0.96)',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    padding: 0,
    "& [class*='MuiButton-label']": {
      height: '100%',
    },
    [`& .${classes.logo}`]: {
      width: '50%',
      margin: theme.spacing(0, 2),
    },
    "& [class*='MuiButton-endIcon']": {
      borderTopRightRadius: theme.typography.pxToRem(4),
      borderBottomRightRadius: theme.typography.pxToRem(4),
      width: theme.typography.pxToRem(54),
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.palette.grey.A400,
    },
  },

  [`& .${classes.logo}`]: {},

  [`& .${classes.root}`]: {
    position: 'fixed',
    bottom: 0,
    padding: theme.spacing(1, 0, 2),
    width: '100%',
    backgroundColor: 'rgba(17,17,17,0.96)',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.close}`]: {
    textAlign: 'right',
    "& [class*='MuiButtonBase-root']": {
      color: 'rgba(255, 255, 255, 0.75)',
    },
  },

  [`& .${classes.logos}`]: {
    display: 'flex',
    justifyContent: 'center',
    '& img': {
      maxWidth: '5rem',
      maxHeight: '3rem',
      margin: theme.spacing(0, 0.5),
    },
  },

  [`& .${classes.info}`]: {
    height: '100%',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none',
      padding: theme.spacing(0),
    },
    [`& .${classes.description}`]: {
      color: 'rgba(255, 255, 255, 0.75)',
      display: 'inline-block',
      lineHeight: '0.9375rem',
      margin: theme.spacing(1, 0),
    },
  },

  [`& .${classes.description}`]: {},

  [`& .${classes.links}`]: {
    textAlign: 'right',
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
    [`& .${classes.back}`]: {
      lineHeight: 1.3,
      margin: theme.spacing(3, 0),
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(0, 1),
      },
      transition: theme.transitions.create(['color', 'background-color'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.short,
      }),
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.text.primary,
      },
    },
  },

  [`& .${classes.back}`]: {},
}));

const Revzilla = () => {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies();
  const [expanded, setExpanded] = React.useState(true);
  const updateCookie = (state) => {
    const expiryTime = new Date();
    expiryTime.setHours(expiryTime.getHours() + 2);

    if (cookies.eref) {
      const content = cookies.eref;

      if (state == 'shrinked') content.expand = false;
      else if (state == 'expanded') content.expand = true;

      setCookie('eref', content, {
        expires: expiryTime,
      });
    }
  };
  const handleExpand = () => {
    setExpanded(true);
    updateCookie('expanded');
  };

  const handleClose = () => {
    setExpanded(false);
    updateCookie('shrinked');
  };

  const wasShrinked = () => {
    if (cookies.eref) {
      const content = cookies.eref;

      if (!content) return false;
      return !content.expand;
    }
    return false;
  };

  useEffect(() => {
    if (wasShrinked()) handleClose();
  }, []);

  return (
    <Root>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleExpand}
        className={classes.toggle}
        endIcon={<LaunchIcon />}
        size="large">
        <img className={classes.logo} src="/imgs/revzilla.svg" alt="revzilla" />
      </Button>
      <Slide in={expanded} direction="up">
        <div className={classes.root}>
          <Container>
            <div className={classes.close}>
              <IconButton onClick={handleClose} aria-label="Close" size="large">
                <CloseIcon />
              </IconButton>
            </div>
            <Grid container spacing={2}>
              <Grid item md={3} xs={12}>
                <div className={classes.logos}>
                  <img src="/imgs/revzilla.svg" alt="revzilla" />
                  <img src="/imgs/logo.png" alt="Eagle rider" />
                </div>
              </Grid>
              <Grid item md={6} xs={12}>
                <div className={classes.info}>
                  <Typography variant="subtitle2">
                    {t('common_layout_v2:revzilla.header')}
                  </Typography>
                  <Typography variant="caption" className={classes.description}>
                    {t('common_layout_v2:revzilla.des')}
                  </Typography>
                </div>
              </Grid>
              <Grid item md={3} xs={12}>
                <div className={classes.links}>
                  <Link href="https://www.revzilla.com/">
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<LaunchIcon />}
                      className={classes.back}>
                      {t('common_layout_v2:revzilla.back')}
                    </Button>
                  </Link>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleClose}>
                    {t('common_layout_v2:revzilla.continue')}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Container>
        </div>
      </Slide>
    </Root>
  );
};

export default Revzilla;
